export type SmsAudienceFilter =
  | "all"
  | "active_3m"
  | "active_6m"
  | "active_1y"
  | "active_2y"
  | "inactive_6m"
  | "inactive_1y"
  | "inactive_2y";

export type SmsSettingsData = {
  configured: boolean;
  provider: "notify.lk";
  senderId: string;
  welcomeMsg: string;
  appointmentMsg: string;
  appRescheduleMsg: string;
  appCancelMsg: string;
  appReminderMsg: string;
  appHourBeforeReminderMsg: string;
  morningReminderTime: string;
  invoiceMsg: string;
  status: boolean;
  birthdayMsg: string;
  birthdayEnabled: boolean;
  birthdayAudienceFilter: SmsAudienceFilter;
  followUpMsg: string;
  followUpEnabled: boolean;
  followUpDaysAfter: number;
  holidayMsg: string;
  holidayEnabled: boolean;
  holidayAudienceFilter: SmsAudienceFilter;
  holidayDates: { name: string; date: string }[];
  oralHealthTipMsg: string;
  oralHealthTipEnabled: boolean;
  oralHealthTipAudienceFilter: SmsAudienceFilter;
  oralHealthTipDay: number;
  promoMsg: string;
  promoEnabled: boolean;
  promoAudienceFilter: SmsAudienceFilter;
  promoMonths: number[];
  promoDay: number;
};

export type SmsMessageTypeConfig = {
  field: keyof Pick<
    SmsSettingsData,
    | "welcomeMsg"
    | "appointmentMsg"
    | "appReminderMsg"
    | "appHourBeforeReminderMsg"
    | "appRescheduleMsg"
    | "appCancelMsg"
    | "invoiceMsg"
  >;
  type: string;
  label: string;
  description: string;
  placeholders: string[];
};

export type PromotionMessageTypeConfig = {
  field: keyof Pick<
    SmsSettingsData,
    "birthdayMsg" | "followUpMsg" | "holidayMsg" | "oralHealthTipMsg" | "promoMsg"
  >;
  enabledField: keyof Pick<
    SmsSettingsData,
    | "birthdayEnabled"
    | "followUpEnabled"
    | "holidayEnabled"
    | "oralHealthTipEnabled"
    | "promoEnabled"
  >;
  audienceField?: keyof Pick<
    SmsSettingsData,
    | "birthdayAudienceFilter"
    | "holidayAudienceFilter"
    | "oralHealthTipAudienceFilter"
    | "promoAudienceFilter"
  >;
  type: string;
  label: string;
  description: string;
  placeholders: string[];
};

export const SMS_MESSAGE_TYPES: SmsMessageTypeConfig[] = [
  {
    field: "welcomeMsg",
    type: "welcome",
    label: "Patient welcome",
    description: "Sent when a new patient is registered",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{registration_number}}"],
  },
  {
    field: "appointmentMsg",
    type: "appointment",
    label: "Appointment confirmation",
    description: "Sent when an appointment is booked",
    placeholders: [
      "{{patient_name}}",
      "{{patient_title}}",
      "{{doctor_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
      "{{token_number}}",
    ],
  },
  {
    field: "appReminderMsg",
    type: "reminder",
    label: "Morning appointment reminder",
    description: "Sent on the appointment day at your morning reminder time",
    placeholders: [
      "{{patient_name}}",
      "{{patient_title}}",
      "{{doctor_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
      "{{token_number}}",
    ],
  },
  {
    field: "appHourBeforeReminderMsg",
    type: "reminder-hour",
    label: "One hour before reminder",
    description: "Sent automatically one hour before the appointment starts",
    placeholders: [
      "{{patient_name}}",
      "{{patient_title}}",
      "{{doctor_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
      "{{token_number}}",
    ],
  },
  {
    field: "appRescheduleMsg",
    type: "reschedule",
    label: "Appointment reschedule",
    description: "Sent when an appointment is moved because its dentist is absent",
    placeholders: [
      "{{patient_name}}",
      "{{patient_title}}",
      "{{doctor_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "appCancelMsg",
    type: "cancel",
    label: "Appointment cancelled",
    description:
      "Sent when appointments are cancelled because their dentist is absent. Date and time are the cancelled visit.",
    placeholders: [
      "{{patient_name}}",
      "{{patient_title}}",
      "{{doctor_name}}",
      "{{clinic_name}}",
      "{{appointment_date}}",
      "{{appointment_time}}",
    ],
  },
  {
    field: "invoiceMsg",
    type: "invoice",
    label: "Invoice notification",
    description: "Sent when an invoice link is shared with a patient",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{invoice_link}}"],
  },
];

export const PROMOTION_MESSAGE_TYPES: PromotionMessageTypeConfig[] = [
  {
    field: "birthdayMsg",
    enabledField: "birthdayEnabled",
    audienceField: "birthdayAudienceFilter",
    type: "birthday",
    label: "Birthday wishes",
    description:
      "Sent on patient birthdays for patients with promotional SMS turned on (and a date of birth)",
    placeholders: ["{{patient_name}}", "{{clinic_name}}"],
  },
  {
    field: "followUpMsg",
    enabledField: "followUpEnabled",
    type: "follow-up",
    label: "Treatment completion follow-up",
    description: "Sent after treatment is completed",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{treatment_name}}"],
  },
  {
    field: "holidayMsg",
    enabledField: "holidayEnabled",
    audienceField: "holidayAudienceFilter",
    type: "holiday",
    label: "Holiday greetings",
    description: "Sent to all patients on configured holiday dates",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{holiday_name}}"],
  },
  {
    field: "oralHealthTipMsg",
    enabledField: "oralHealthTipEnabled",
    audienceField: "oralHealthTipAudienceFilter",
    type: "oral-health-tip",
    label: "Oral health tips (monthly)",
    description: "Sent monthly on the configured day",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{health_tip}}"],
  },
  {
    field: "promoMsg",
    enabledField: "promoEnabled",
    audienceField: "promoAudienceFilter",
    type: "promo",
    label: "Promotional offers",
    description: "Sent during slow periods or manually to selected patients",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{offer_details}}"],
  },
];

export const AUDIENCE_FILTER_OPTIONS: {
  value: SmsAudienceFilter;
  label: string;
  promoOnly?: boolean;
}[] = [
  { value: "all", label: "All patients" },
  { value: "active_3m", label: "Active within 3 months" },
  { value: "active_6m", label: "Active within 6 months" },
  { value: "active_1y", label: "Active within 1 year" },
  { value: "active_2y", label: "Active within 2 years" },
  { value: "inactive_6m", label: "Inactive for 6 months", promoOnly: true },
  { value: "inactive_1y", label: "Inactive for 1 year", promoOnly: true },
  { value: "inactive_2y", label: "Inactive for 2 years", promoOnly: true },
];

export const DEFAULT_AUDIENCE_FILTER: Record<string, SmsAudienceFilter> = {
  birthday: "active_1y",
  holiday: "active_1y",
  "oral-health-tip": "active_6m",
  promo: "active_1y",
};

export function audienceOptionsForTemplate(type: string) {
  if (type === "promo") return AUDIENCE_FILTER_OPTIONS;
  return AUDIENCE_FILTER_OPTIONS.filter((option) => !option.promoOnly);
}

export function formatAudienceFilterLabel(value: string | null | undefined) {
  const match = AUDIENCE_FILTER_OPTIONS.find((option) => option.value === value);
  return match?.label || "—";
}

export const MONTH_OPTIONS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

export function isSmsTypeConfigured(
  settings: SmsSettingsData | null,
  field: SmsMessageTypeConfig["field"] | PromotionMessageTypeConfig["field"]
) {
  return Boolean(String(settings?.[field] || "").trim());
}

export function formatSmsTypeLabel(value: string | null | undefined) {
  if (!value) return "—";
  const match =
    SMS_MESSAGE_TYPES.find((item) => item.type === value) ||
    PROMOTION_MESSAGE_TYPES.find((item) => item.type === value);
  if (match) return match.label;
  if (value === "reminder-hour") return "One hour before reminder";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, " ");
}
